import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Copy, Link, Trash2, Users, Clock } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BoardShareLink } from '../types';
import { createShareLink, getShareLinksForBoard, deactivateShareLink } from '../firebase/firestore';

interface ShareBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  boardName: string;
  currentUserEmail: string;
  currentUserId: string;
}

const ShareBoardModal: React.FC<ShareBoardModalProps> = ({
  isOpen,
  onClose,
  boardId,
  boardName,
  currentUserEmail,
  currentUserId,
}) => {
  const [shareLinks, setShareLinks] = useState<BoardShareLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && boardId) {
      loadShareLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadShareLinks is stable
  }, [isOpen, boardId]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const loadShareLinks = async () => {
    try {
      const links = await getShareLinksForBoard(boardId);
      setShareLinks(links);
    } catch (error) {
      console.error('Error loading share links:', error);
    }
  };

  const handleCreateShareLink = async () => {
    setIsLoading(true);
    try {
      const shareLinkId = await createShareLink(boardId, boardName, currentUserId, currentUserEmail, selectedRole);
      const shareUrl = `${window.location.origin}/join/${shareLinkId}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(shareUrl);
      
      // Reload share links
      await loadShareLinks();
      
      // Reset copied state after 3 seconds
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      console.error('Error creating share link:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async (shareLinkId: string) => {
    const shareUrl = `${window.location.origin}/join/${shareLinkId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(shareUrl);
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleDeactivateLink = async (shareLinkId: string) => {
    try {
      await deactivateShareLink(shareLinkId);
      await loadShareLinks();
    } catch (error) {
      console.error('Error deactivating share link:', error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'editor':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString();
  };

  const getTimeRemaining = (expiresAt: Date) => {
    const now = new Date();
    const diffInDays = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) return 'Expired';
    if (diffInDays === 0) return 'Expires today';
    if (diffInDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffInDays} days`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Share Board</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Create New Share Link */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-blue-800 mb-3">Create New Share Link</h3>
              <div className="flex items-center space-x-3">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'editor' | 'viewer')}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  onClick={handleCreateShareLink}
                  disabled={isLoading}
                  className="btn-primary text-sm px-4 py-2 flex items-center space-x-2"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Link size={16} />
                  )}
                  <span>{isLoading ? 'Creating...' : 'Create Link'}</span>
                </button>
              </div>
              {copiedLink && (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center space-x-2">
                  <Copy size={14} />
                  <span>Link copied to clipboard!</span>
                </div>
              )}
            </div>

            {/* Existing Share Links */}
            <div>
              <h3 className="text-sm font-medium text-gray-800 mb-3">Active Share Links</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {shareLinks.length === 0 ? (
                  <div className="text-center py-8">
                    <Share2 size={48} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No active share links</p>
                    <p className="text-xs text-gray-400 mt-1">Create a share link to get started</p>
                  </div>
                ) : (
                  shareLinks.map((link) => (
                    <motion.div
                      key={link.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass-effect rounded-xl p-4 border border-white/30"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(link.role)}`}>
                              {link.role}
                            </span>
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <Users size={12} />
                              <span>{link.accessCount} access{link.accessCount !== 1 ? 'es' : ''}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                            <Clock size={12} />
                            <span>{getTimeRemaining(link.expiresAt!)}</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Created {formatDate(link.createdAt)}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleCopyLink(link.id)}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                        >
                          <Copy size={16} />
                          <span className="text-sm font-medium">Copy Link</span>
                        </button>
                        <button
                          onClick={() => handleDeactivateLink(link.id)}
                          className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-white/30">
              <p className="text-xs text-gray-500 text-center">
                Share links expire in 30 days. Anyone with the link can join the board with the specified role.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareBoardModal;
